import { fetchAdminSettings, patchAdminSettings, type IAdminSettings } from '@/api/admin';
import LoadingWrapper from '@/components/loading-wrapper';
import { Button } from '@/components/ui/button';
import { CardContent } from '@/components/ui/card';
import SettingsWidgetCard from './settings-widget-card';
import { InputGroup, InputGroupInput } from '@/components/ui/input-group';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { $updateFeatures } from '@/entities/features-entity';
import { useUnit } from 'effector-react';
import { CheckCircle2Icon, CircleAlertIcon, GlobeIcon } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { toast } from 'sonner';

const MIN_DAILY_LIMIT = 1;
const MAX_DAILY_LIMIT = 100;
const MIN_CHECK_INTERVAL_MS = 60_000;
const MAX_CHECK_INTERVAL_MS = 3_600_000;
const MIN_NS_TIMEOUT_DAYS = 1;
const MAX_NS_TIMEOUT_DAYS = 30;
const IPV4_REGEX = /^(?:(?:25[0-5]|2[0-4]\d|[01]?\d\d?)\.){3}(?:25[0-5]|2[0-4]\d|[01]?\d\d?)$/;
const CLOUDFLARE_ACCOUNT_ID_REGEX = /^[a-f0-9]{32}$/i;

type DomainsInfrastructureMissing = NonNullable<IAdminSettings['domainsInfrastructureMissing']>[number];

const INFRASTRUCTURE_MISSING_LABELS: Record<DomainsInfrastructureMissing, string> = {
	cloudflare_token: 'Cloudflare API Token',
	cloudflare_account_id: 'Cloudflare Account ID',
	origin_ip: 'Origin IP',
	nginx_enabled: 'DOMAIN_NGINX_ENABLED=true в env back-сервиса',
	nginx_ssh_host: 'DOMAIN_NGINX_SSH_HOST или SSH_HOST в env back-сервиса',
};

const INFRASTRUCTURE_CHECKLIST: DomainsInfrastructureMissing[] = [
	'cloudflare_token',
	'cloudflare_account_id',
	'origin_ip',
	'nginx_enabled',
	'nginx_ssh_host',
];

function resolveDomainsFeatureEnabled(settings: IAdminSettings): boolean {
	return Boolean(settings.domainsEnabled && settings.domainsInfrastructureReady);
}

type FormInfraInput = {
	serverMissing: DomainsInfrastructureMissing[];
	cfToken: string;
	cfTokenMasked: string | null;
	cfAccountId: string;
	originIp: string;
};

function getEffectiveInfrastructureMissing({
	serverMissing,
	cfToken,
	cfTokenMasked,
	cfAccountId,
	originIp,
}: FormInfraInput): DomainsInfrastructureMissing[] {
	let missing = [...serverMissing];

	if (missing.includes('cloudflare_account_id') && CLOUDFLARE_ACCOUNT_ID_REGEX.test(cfAccountId.trim())) {
		missing = missing.filter(key => key !== 'cloudflare_account_id');
	}

	if (missing.includes('cloudflare_token') && (cfTokenMasked || cfToken.trim().length > 0)) {
		missing = missing.filter(key => key !== 'cloudflare_token');
	}

	if (missing.includes('origin_ip') && IPV4_REGEX.test(originIp.trim())) {
		missing = missing.filter(key => key !== 'origin_ip');
	}

	return missing;
}

function isEnvInfrastructureMissing(missing: DomainsInfrastructureMissing[]): boolean {
	return missing.some(key => key === 'nginx_enabled' || key === 'nginx_ssh_host');
}

export default function DomainSettingsWidget() {
	const updateFeatures = useUnit($updateFeatures);
	const [domainsEnabled, setDomainsEnabled] = useState(false);
	const [cfToken, setCfToken] = useState('');
	const [cfTokenMasked, setCfTokenMasked] = useState<string | null>(null);
	const [cfOriginCaKey, setCfOriginCaKey] = useState('');
	const [cfOriginCaKeyMasked, setCfOriginCaKeyMasked] = useState<string | null>(null);
	const [cfAccountId, setCfAccountId] = useState('');
	const [originIp, setOriginIp] = useState('');
	const [dailyLimitInput, setDailyLimitInput] = useState('');
	const [checkIntervalInput, setCheckIntervalInput] = useState('');
	const [nsTimeoutInput, setNsTimeoutInput] = useState('');
	const [domainsInfrastructureMissing, setDomainsInfrastructureMissing] = useState<DomainsInfrastructureMissing[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [isSaving, setIsSaving] = useState(false);

	function applySettings(settings: IAdminSettings) {
		setDomainsEnabled(settings.domainsEnabled ?? false);
		setCfToken('');
		setCfTokenMasked(settings.domainCloudflareApiToken ?? null);
		setCfOriginCaKey('');
		setCfOriginCaKeyMasked(settings.domainCloudflareOriginCaKey ?? null);
		setCfAccountId(settings.domainCloudflareAccountId ?? '');
		setOriginIp(settings.domainOriginIp ?? '');
		setDailyLimitInput(String(settings.domainDailyLimitPerUser ?? 10));
		setCheckIntervalInput(String(settings.domainCheckIntervalMs ?? 180_000));
		setNsTimeoutInput(String(settings.domainNsTimeoutDays ?? 7));
		setDomainsInfrastructureMissing(settings.domainsInfrastructureMissing ?? []);
	}

	useEffect(() => {
		let cancelled = false;

		async function load() {
			try {
				const settings = await fetchAdminSettings();
				if (!cancelled) {
					applySettings(settings);
				}
			} finally {
				if (!cancelled) setIsLoading(false);
			}
		}

		void load();

		return () => {
			cancelled = true;
		};
	}, []);

	function getFormInfrastructureMissing(): DomainsInfrastructureMissing[] {
		return getEffectiveInfrastructureMissing({
			serverMissing: domainsInfrastructureMissing,
			cfToken,
			cfTokenMasked,
			cfAccountId,
			originIp,
		});
	}

	function handleDomainsEnabledChange(checked: boolean) {
		if (!checked) {
			setDomainsEnabled(false);
			return;
		}

		const effectiveMissing = getFormInfrastructureMissing();
		if (effectiveMissing.length === 0) {
			setDomainsEnabled(true);
			return;
		}

		if (isEnvInfrastructureMissing(effectiveMissing)) {
			const missing = effectiveMissing.map(key => INFRASTRUCTURE_MISSING_LABELS[key]).join(', ');
			toast.error(`Сначала настройте инфраструктуру: ${missing}`);
			return;
		}

		toast.error('Сначала нажмите «Сохранить»');
	}

	async function handleSubmit(e: FormEvent) {
		e.preventDefault();

		const domainDailyLimitPerUser = Number(dailyLimitInput);
		const domainCheckIntervalMs = Number(checkIntervalInput);
		const domainNsTimeoutDays = Number(nsTimeoutInput);

		if (
			!dailyLimitInput.trim() ||
			!Number.isInteger(domainDailyLimitPerUser) ||
			domainDailyLimitPerUser < MIN_DAILY_LIMIT ||
			domainDailyLimitPerUser > MAX_DAILY_LIMIT
		) {
			toast.error(`Лимит доменов должен быть от ${MIN_DAILY_LIMIT} до ${MAX_DAILY_LIMIT}`);
			return;
		}

		if (
			!checkIntervalInput.trim() ||
			!Number.isInteger(domainCheckIntervalMs) ||
			domainCheckIntervalMs < MIN_CHECK_INTERVAL_MS ||
			domainCheckIntervalMs > MAX_CHECK_INTERVAL_MS
		) {
			toast.error(`Интервал проверки: от ${MIN_CHECK_INTERVAL_MS / 1000} до ${MAX_CHECK_INTERVAL_MS / 1000} сек`);
			return;
		}

		if (
			!nsTimeoutInput.trim() ||
			!Number.isInteger(domainNsTimeoutDays) ||
			domainNsTimeoutDays < MIN_NS_TIMEOUT_DAYS ||
			domainNsTimeoutDays > MAX_NS_TIMEOUT_DAYS
		) {
			toast.error(`Таймаут NS: от ${MIN_NS_TIMEOUT_DAYS} до ${MAX_NS_TIMEOUT_DAYS} дней`);
			return;
		}

		const trimmedOriginIp = originIp.trim();
		if (trimmedOriginIp && !IPV4_REGEX.test(trimmedOriginIp)) {
			toast.error('Origin IP: укажите корректный IPv4-адрес');
			return;
		}

		const trimmedAccountId = cfAccountId.trim();
		if (trimmedAccountId && !CLOUDFLARE_ACCOUNT_ID_REGEX.test(trimmedAccountId)) {
			toast.error('Cloudflare Account ID: укажите 32 hex-символа');
			return;
		}

		const patch: Partial<IAdminSettings> = {
			domainsEnabled,
			domainOriginIp: trimmedOriginIp || null,
			domainDailyLimitPerUser,
			domainCheckIntervalMs,
			domainNsTimeoutDays,
		};

		if (cfToken.trim()) {
			patch.domainCloudflareApiToken = cfToken.trim();
		}

		if (cfOriginCaKey.trim()) {
			patch.domainCloudflareOriginCaKey = cfOriginCaKey.trim();
		}

		patch.domainCloudflareAccountId = trimmedAccountId || null;

		setIsSaving(true);
		try {
			const settings = await patchAdminSettings(patch);
			applySettings(settings);
			updateFeatures({ domainsEnabled: resolveDomainsFeatureEnabled(settings) });
			toast.success('Настройки доменов сохранены');
		} catch {
			// toast показывается в axios interceptor
		} finally {
			setIsSaving(false);
		}
	}

	return (
		<SettingsWidgetCard
			id='domains'
			icon={GlobeIcon}
			title='Домены'
			description='Cloudflare и лимиты пользовательских доменов'
			titleClassName='text-chart-1'
			iconWrapperClassName='bg-chart-1/15'
		>
			<LoadingWrapper show={isSaving}>
				<CardContent className='px-4 py-4'>
					<form className='flex flex-col gap-4' onSubmit={handleSubmit}>
						<div className='rounded-lg border border-border bg-muted/40 px-3 py-3'>
							<p className='text-sm font-medium mb-2'>Готовность инфраструктуры</p>
							<ul className='flex flex-col gap-1.5'>
								{INFRASTRUCTURE_CHECKLIST.map(key => {
									const isMissing = domainsInfrastructureMissing.includes(key);
									return (
										<li key={key} className='flex items-start gap-2 text-sm'>
											{isMissing ? (
												<CircleAlertIcon className='mt-0.5 size-4 shrink-0 text-destructive' />
											) : (
												<CheckCircle2Icon className='mt-0.5 size-4 shrink-0 text-chart-1' />
											)}
											<span className={isMissing ? 'text-muted-foreground' : undefined}>
												{INFRASTRUCTURE_MISSING_LABELS[key]}
											</span>
										</li>
									);
								})}
							</ul>
						</div>

						<div className='flex items-center justify-between gap-3'>
							<div className='flex flex-col gap-0.5'>
								<Label htmlFor='domains-enabled'>Добавление доменов</Label>
								<span className='text-xs text-muted-foreground'>
									Разрешить пользователям добавлять свои домены
								</span>
							</div>
							<Switch
								id='domains-enabled'
								checked={domainsEnabled}
								onCheckedChange={handleDomainsEnabledChange}
								disabled={isLoading || getFormInfrastructureMissing().length > 0}
							/>
						</div>

						<Separator />

						<div className='flex flex-col gap-1.5'>
							<Label htmlFor='cf-api-token'>Cloudflare API Token</Label>
							<span className='text-xs text-muted-foreground'>
								{cfTokenMasked
									? `Токен задан (${cfTokenMasked}). Оставьте поле пустым, чтобы не менять.`
									: 'Токен не задан. Введите и сохраните.'}
							</span>
							<span className='text-xs text-muted-foreground'>
								Права: Account Settings Read, Zone Edit, DNS Edit, SSL and Certificates Edit (All zones).
							</span>
							<InputGroup>
								<InputGroupInput
									id='cf-api-token'
									type='password'
									autoComplete='off'
									placeholder='••••••••'
									value={cfToken}
									onChange={e => setCfToken(e.target.value)}
									disabled={isLoading}
								/>
							</InputGroup>
						</div>

						<div className='flex flex-col gap-1.5'>
							<Label htmlFor='cf-origin-ca-key'>Cloudflare Origin CA Key</Label>
							<span className='text-xs text-muted-foreground'>
								{cfOriginCaKeyMasked
									? `Ключ задан (${cfOriginCaKeyMasked}). Оставьте поле пустым, чтобы не менять.`
									: 'Не задан. Альтернатива SSL permission у API Token.'}
							</span>
							<span className='text-xs text-muted-foreground'>
								SSL/TLS → Origin Server → Origin CA Key (начинается с v1.0-).
							</span>
							<InputGroup>
								<InputGroupInput
									id='cf-origin-ca-key'
									type='password'
									autoComplete='off'
									placeholder='v1.0-...'
									value={cfOriginCaKey}
									onChange={e => setCfOriginCaKey(e.target.value)}
									disabled={isLoading}
								/>
							</InputGroup>
						</div>

						<div className='flex flex-col gap-1.5'>
							<Label htmlFor='cf-account-id'>Cloudflare Account ID</Label>
							<span className='text-xs text-muted-foreground'>
								32 hex-символа из URL дашборда или Workers &amp; Pages → Account ID
							</span>
							<InputGroup>
								<InputGroupInput
									id='cf-account-id'
									autoComplete='off'
									placeholder='758410f01a395281ae896d72887473e5'
									value={cfAccountId}
									onChange={e => setCfAccountId(e.target.value)}
									disabled={isLoading}
								/>
							</InputGroup>
						</div>

						<div className='flex flex-col gap-1.5'>
							<Label htmlFor='domain-origin-ip'>Origin IP</Label>
							<span className='text-xs text-muted-foreground'>IP сервера для A-записи в Cloudflare</span>
							<InputGroup>
								<InputGroupInput
									id='domain-origin-ip'
									placeholder='203.0.113.10'
									value={originIp}
									onChange={e => setOriginIp(e.target.value)}
									disabled={isLoading}
								/>
							</InputGroup>
						</div>

						<Separator />

						<div className='flex flex-col gap-1.5'>
							<Label htmlFor='domain-daily-limit'>Доменов в день на пользователя</Label>
							<InputGroup>
								<InputGroupInput
									id='domain-daily-limit'
									type='number'
									inputMode='numeric'
									min={MIN_DAILY_LIMIT}
									max={MAX_DAILY_LIMIT}
									value={dailyLimitInput}
									onChange={e => setDailyLimitInput(e.target.value)}
									disabled={isLoading}
								/>
							</InputGroup>
						</div>

						<div className='flex flex-col gap-1.5'>
							<Label htmlFor='domain-check-interval'>Интервал проверки DNS (мс)</Label>
							<InputGroup>
								<InputGroupInput
									id='domain-check-interval'
									type='number'
									inputMode='numeric'
									min={MIN_CHECK_INTERVAL_MS}
									max={MAX_CHECK_INTERVAL_MS}
									value={checkIntervalInput}
									onChange={e => setCheckIntervalInput(e.target.value)}
									disabled={isLoading}
								/>
							</InputGroup>
						</div>

						<div className='flex flex-col gap-1.5'>
							<Label htmlFor='domain-ns-timeout'>Таймаут ожидания NS (дней)</Label>
							<InputGroup>
								<InputGroupInput
									id='domain-ns-timeout'
									type='number'
									inputMode='numeric'
									min={MIN_NS_TIMEOUT_DAYS}
									max={MAX_NS_TIMEOUT_DAYS}
									value={nsTimeoutInput}
									onChange={e => setNsTimeoutInput(e.target.value)}
									disabled={isLoading}
								/>
							</InputGroup>
						</div>

						<Button type='submit' className='self-start' disabled={isLoading || isSaving}>
							Сохранить
						</Button>
					</form>
				</CardContent>
			</LoadingWrapper>
		</SettingsWidgetCard>
	);
}
